import { z } from 'zod';
import { agendaStatsModes, type AgendaStatsMode } from '../enums.js';

/**
 * Réglages d'affichage de l'agenda, **par utilisateur et persistés côté serveur**.
 *
 * La V1 les gardait dans le stockage local du navigateur (docs/SCREENS.md, tableau « Réglages
 * d'affichage ») : ils ne suivaient pas le praticien d'un poste à l'autre, et un poste partagé au
 * secrétariat les mélangeait. Ce sont des champs typés, jamais un blob : chaque valeur appartient
 * à un ensemble fermé, vérifié ici et par une contrainte en base.
 *
 * Les trois préréglages de densité et les pas de grille sont ceux de la V1 — le design est figé
 * (CLAUDE.md §6), on ne réinvente ni les hauteurs ni les durées.
 */

/** Hauteur d'un créneau en pixels : compact, standard, confort. */
export const agendaDensities = [53, 87, 133] as const;
export type AgendaDensity = (typeof agendaDensities)[number];

/** Durée d'un créneau de la grille, en minutes. */
export const agendaSlotMinutes = [5, 10, 15, 30, 60] as const;
export type AgendaSlotMinutes = (typeof agendaSlotMinutes)[number];

/** Aimantation du glisser-déposer et du redimensionnement, en minutes. */
export const agendaSnapMinutes = [5, 15, 30, 60] as const;
export type AgendaSnapMinutes = (typeof agendaSnapMinutes)[number];

/* -------------------------------------------------------------------------- */
/* Entrée                                                                      */
/* -------------------------------------------------------------------------- */

/**
 * `PUT` de remplacement complet, comme les préférences du compte : un champ absent serait ambigu
 * entre « inchangé » et « remis par défaut ».
 */
export const agendaPreferencesUpdateSchema = z
  .object({
    /** Première heure de la grille, incluse. */
    startHour: z.int().min(0).max(23),
    /** Dernière heure de la grille, exclue : 24 signifie minuit du lendemain. */
    endHour: z.int().min(1).max(24),
    slotMinutes: z.union([
      z.literal(5),
      z.literal(10),
      z.literal(15),
      z.literal(30),
      z.literal(60),
    ]),
    density: z.union([z.literal(53), z.literal(87), z.literal(133)]),
    /** Colonnes rendues en vue semaine. Au moins un jour, jamais deux fois le même. */
    visibleWeekdays: z
      .array(z.int().min(0).max(6))
      .min(1)
      .max(7)
      .refine(
        (days) => new Set(days).size === days.length,
        'Un jour ne peut apparaître qu’une fois',
      ),
    showHolidays: z.boolean(),
    snapMinutes: z.union([z.literal(5), z.literal(15), z.literal(30), z.literal(60)]),
    statsMode: z.enum(agendaStatsModes),
  })
  .strict()
  .refine((p) => p.endHour > p.startHour, {
    error: 'L’heure de fin doit suivre l’heure de début',
    path: ['endHour'],
  });
export type AgendaPreferencesUpdateInput = z.infer<typeof agendaPreferencesUpdateSchema>;

/* -------------------------------------------------------------------------- */
/* Sortie                                                                      */
/* -------------------------------------------------------------------------- */

export interface AgendaPreferencesDto {
  startHour: number;
  endHour: number;
  slotMinutes: AgendaSlotMinutes;
  density: AgendaDensity;
  /** Norme JavaScript : 0 = dimanche, 6 = samedi. */
  visibleWeekdays: number[];
  showHolidays: boolean;
  snapMinutes: AgendaSnapMinutes;
  statsMode: AgendaStatsMode;
}

/**
 * Valeurs de départ, identiques à celles de la V1 : 7 h → 21 h, créneaux de 30 min, densité
 * standard, lundi → samedi, jours fériés affichés, aimantation au quart d'heure, statistiques
 * « marquées ». Le serveur les renvoie tant que l'utilisateur n'a rien enregistré ; le front s'en
 * sert aussi pour son bouton « réinitialiser ».
 */
export const agendaPreferencesDefaults: AgendaPreferencesDto = {
  startHour: 7,
  endHour: 21,
  slotMinutes: 30,
  density: 87,
  visibleWeekdays: [1, 2, 3, 4, 5, 6],
  showHolidays: true,
  snapMinutes: 15,
  statsMode: 'marked',
};
